import React, { memo, useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { Box, Flex, Input, useDisclosure } from '@chakra-ui/react';
import { Button, TextInput, Unform } from 'components';
import { debounce } from 'utils/debounce';
import { FormHandles } from '@unform/core';
import { objToQuery, queryToObj } from 'utils/query';
import { routePaths } from 'constants/routes';
import { RouteComponentProps } from 'react-router';
import { getLawyers, Lawyer, saveLawyer } from 'services/lawyers';
import { HiPlusSm as Plus } from 'react-icons/hi';
import { ModalCustomer } from 'components/CustomerModal';

import { SubmitHandler, useForm } from 'react-hook-form';
import { openNotification } from 'utils/notification';

interface IProps {
  history: RouteComponentProps['history'];
  setUpdate: React.Dispatch<React.SetStateAction<boolean>>;
}
interface IFormData {
  name: string;
  status: string;
  lawyer: string;
}

export const ToolbarComponent: React.FC<IProps> = ({ history, setUpdate }) => {
  const formRef = useRef<FormHandles>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lawyerOptions, setLawyerOptions] = useState<Lawyer[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { name, status, lawyer } = useMemo(() => {
    return queryToObj(history.location.search) as {
      name?: string;
      status?: string;
      lawyer?: string;
    };
  }, [history.location.search]);

  const handleSearch = useCallback(() => {
    const formData = formRef.current?.getData() as IFormData;

    const query = objToQuery({ ...formData, page: 0 });

    history.replace(`${routePaths.lawyerManagement}?${query}`);
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await getLawyers();
      setLawyerOptions(data);
    })();
  }, []);

  const { register, handleSubmit, reset } = useForm<{ name: string }>();

  const submit: SubmitHandler<{ name: string }> = async data => {
    try {
      const name = data.name.trim();
      // eslint-disable-next-line no-extra-boolean-cast
      if (name === '') {
        openNotification('Informe o nome do advogado!', 'warn');
        return;
      }

      setIsLoading(true);
      const adv: { id: number; name: string; status: boolean } = {
        id: 0,
        name,
        status: true,
      };

      await saveLawyer(adv);
      setIsLoading(false);
      onClose();
      reset();
      openNotification('Advogado cadastrado com sucesso!', 'success');
      setUpdate(oldState => !oldState);
    } catch (err) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const inputTeclado = event.key;
    const padrao = /^[A-Za-z\s]+$/;
    if (!padrao.test(inputTeclado) && inputTeclado !== ' ') {
      event.preventDefault();
    }
  };

  return (
    <>
      <Unform initialData={{ name, status, lawyer }} formRef={formRef}>
        <Flex justify="space-between" alignItems="center">
          <Flex mt="1rem" mb="1rem">
            <Box w="30rem" mr="1rem">
              <TextInput
                name="name"
                notErrorMargin
                backgroundColor="#fff"
                leftIcon={{ name: 'search', color: 1100 }}
                placeholder="Busca por advogado"
                onInput={debounce(handleSearch, 1000)}
                onKeyPress={(ev: any) => {
                  const keyCode = ev.keyCode ? ev.keyCode : ev.which ? ev.which : ev.charCode;
                  if (keyCode === 13) {
                    if (ev.stopPropagation) {
                      ev.stopPropagation();
                      ev.preventDefault();
                    }
                  }
                }}
              />
            </Box>
          </Flex>
          <Box w="15rem" mt="-1.5rem">
            <Button
              fullWidth
              type="button"
              variant="accept"
              onClick={onOpen}
              style={{
                lineHeight: '0.9rem',
              }}
            >
              <Plus color="#fff" fontSize="1.8rem" /> Novo Advogado
            </Button>
          </Box>
        </Flex>
      </Unform>

      <ModalCustomer title="Adicionar Advogado" isOpen={isOpen} onClose={onClose}>
        <form onSubmit={handleSubmit(submit)}>
          <Flex mb="2rem">
            <Input
              placeholder="Nome do Advogado"
              {...register('name')}
              onKeyPress={handleKeyPress}
            />
          </Flex>
          <Flex flex="1" justify="space-between">
            <Button type="button" variant="secondary" outline onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={isLoading}>
              Adicionar
            </Button>
          </Flex>
        </form>
      </ModalCustomer>
    </>
  );
};
export const Toolbar = memo(ToolbarComponent);
